'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { SelectOptionType } from '@portals/ui-kit';
import { currentZone } from '@portal-root/geo-utils';
import { useLocalStorage } from '@portals/external-services-header/lib/_hooks/use-local-storage';
import { useAnalytics } from '@/_hooks/use-analytics';
import { COUNTRIES_CONFIG } from '@/_constants/general';
import { ECountries } from '@/_types';
import { EAnalytics } from '@/_constants/analytics';
import {
  EHeaderModifier,
  THeaderConfig,
} from '@/_components/custom-header/_types';
import { useWindowSize } from '@/_hooks/use-window-size';
import { isDesktop } from '@/_utils/is-desktop';
import { getLocaleUrl } from './_utils/get-locale-url';
import { getOriginByCountry } from './_utils/get-origin-by-country';
import { getCountryOptions } from './_utils/get-country-options';
import { getUrlParams } from './_utils/get-url-locale-params';
import {
  HEADER_DESKTOP_HEIGHT,
  BLOCK_WITH_SELECTORS_HEIGHT_MOBILE,
  MENU_HEIGHT_MOBILE,
  TOP_SCROLL_TOLERANCE,
  BREACKPOINT_DESKTOP_MIN,
} from './_constants';
import { HeaderObserver } from './_components/header-observer';
import { HeaderDesktop } from './header-desktop';
import { HeaderMobile } from './header-mobile';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Custom-header';

const CustomHeader = (config: THeaderConfig) => {
  const sendEvent = useAnalytics();
  const { width } = useWindowSize();
  const { mobileWebView } = useLocalStorage();

  const scrollHeaderY = useRef(0);
  const scrollY = useRef(0);
  const mobileHeaderRef = useRef<HTMLDivElement | null>(null);
  const desktopHeaderRef = useRef<HTMLElement | null>(null);

  const isDesktopWidth = isDesktop({
    width,
    breakpointMin: BREACKPOINT_DESKTOP_MIN,
  });

  const threshold = isDesktopWidth
    ? HEADER_DESKTOP_HEIGHT
    : BLOCK_WITH_SELECTORS_HEIGHT_MOBILE;

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [headerModifier, setHeaderModifier] = useState(
    EHeaderModifier.TRANSPARENT,
  );
  const currentCountry = getUrlParams().country;
  const countries = useMemo(
    () =>
      currentCountry === ECountries.TR
        ? config.countries
        : getCountryOptions(currentCountry),
    [config.countries, currentCountry],
  );

  const isTurkey = currentZone.endsWith(ECountries.TR);

  const updateHeaderModifier = useCallback(
    (scrollYPosition: number, isScrollingUp: boolean) => {
      const isAtTopDesktop = isDesktopWidth && scrollYPosition === 0;

      const isAtTopMobile =
        !isDesktopWidth &&
        scrollYPosition <= threshold + MENU_HEIGHT_MOBILE &&
        !isScrollingUp;
      const isAtTopMobileScrollingUp =
        !isDesktopWidth && scrollYPosition <= threshold && isScrollingUp;

      if (isAtTopDesktop || isAtTopMobile || isAtTopMobileScrollingUp) {
        return EHeaderModifier.TRANSPARENT;
      }

      if (scrollYPosition <= threshold) {
        return EHeaderModifier.WHITE_TRANSPARENT;
      }

      return EHeaderModifier.WHITE;
    },
    [isDesktopWidth, threshold],
  );

  const handleChangeLanguage = useCallback(
    ({ id }: SelectOptionType) => {
      const eventConfig = {
        name: EAnalytics.SUPPLIER_LANDING_MENU_LANGUAGE_CHANGE_BUTTON_T,
        params: {
          language: id,
        },
      };

      sendEvent(eventConfig);

      const url = getLocaleUrl({ locale: id });

      window.location.replace(url);
    },
    [sendEvent],
  );

  const handleChangeCountry = useCallback(
    ({ id }: SelectOptionType) => {
      sendEvent({
        name: EAnalytics.SUPPLIER_LANDING_MENU_LANGUAGE_CHANGE_BUTTON_T,
        params: { language: id },
      });

      const defaultLocale = COUNTRIES_CONFIG[id]?.defaultLanguage;
      const relativeUrl = getLocaleUrl({ country: id, locale: defaultLocale });
      const origin = getOriginByCountry(id);
      const finalUrl = origin + relativeUrl;

      window.location.replace(finalUrl);
    },
    [sendEvent],
  );

  useEffect(() => {
    if (isDesktopWidth) {
      mobileHeaderRef.current?.style.setProperty('position', 'static');
    } else {
      desktopHeaderRef.current?.style.setProperty('position', 'absolute');
      desktopHeaderRef.current?.style.setProperty('transform', 'none');
    }
  }, [isDesktopWidth]);

  const updateHeader = useCallback(() => {
    const el = isDesktopWidth
      ? desktopHeaderRef.current
      : mobileHeaderRef.current;
    if (!el || document.body.style.overflow === 'hidden') return;

    const headerHeight = el.offsetHeight;
    const currentY = window.scrollY;
    const deltaY = scrollY.current - currentY;
    const isScrollingUp = currentY < scrollY.current;

    scrollY.current = currentY;

    // Update header Y translation based on scroll delta
    scrollHeaderY.current = Math.min(
      0,
      Math.max(-headerHeight, scrollHeaderY.current + deltaY),
    );

    // Determine if header should be visible
    const shouldShow = currentY > TOP_SCROLL_TOLERANCE || isScrollingUp;

    // Desktop header positioning logic
    const updateDesktopHeaderPosition = () => {
      if (currentY === 0) {
        el.style.position = 'absolute';
      } else if (currentY >= headerHeight) {
        el.style.position = 'fixed';
      }
    };

    // Mobile header positioning logic
    const updateMobileHeaderPosition = () => {
      if (currentY < threshold && !isTurkey) {
        el.style.position = 'absolute';
        el.style.background = 'none';
      } else if (currentY >= headerHeight + threshold || isTurkey) {
        el.style.position = 'fixed';
        el.style.background = 'white';
      }
    };

    // Apply translateY based on scroll position
    const updateTransform = () => {
      const translateY =
        el.style.position === 'fixed' ? scrollHeaderY.current : 0;
      el.style.transform = `translateY(${translateY}px)`;
    };

    // Control visibility and style modifier
    const updateVisibility = () => {
      setIsHeaderVisible(shouldShow);

      const modifier = shouldShow
        ? updateHeaderModifier(currentY, isScrollingUp)
        : EHeaderModifier.TRANSPARENT;

      setHeaderModifier(modifier);
    };

    // Run logic based on screen size
    if (isDesktopWidth) {
      updateDesktopHeaderPosition();
    } else {
      updateMobileHeaderPosition();
    }

    updateTransform();
    updateVisibility();
  }, [isDesktopWidth, isTurkey, threshold, updateHeaderModifier]);

  useEffect(() => {
    if (mobileWebView) {
      return;
    }

    updateHeader();

    window.addEventListener('scroll', updateHeader);

    return () => {
      window.removeEventListener('scroll', updateHeader);
    };
  }, [updateHeader, mobileWebView]);

  const headerContent = isDesktopWidth ? (
    <HeaderDesktop
      handleChangeCountry={handleChangeCountry}
      handleChangeLanguage={handleChangeLanguage}
      modifier={headerModifier}
      {...config}
      countries={countries}
    />
  ) : (
    <HeaderMobile
      ref={mobileHeaderRef}
      handleChangeCountry={handleChangeCountry}
      handleChangeLanguage={handleChangeLanguage}
      modifier={headerModifier}
      {...config}
      countries={countries}
    />
  );

  if (mobileWebView) {
    return null;
  }

  return (
    <header
      ref={desktopHeaderRef}
      className={cn(BLOCK_NAME, {
        [`${BLOCK_NAME}--visible`]: isDesktopWidth && isHeaderVisible,
      })}
    >
      <HeaderObserver />
      {headerContent}
    </header>
  );
};

export default CustomHeader;
